import { Grid, Skeleton, type TablePaginationConfig } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../utils/cn.ts"
import {
  getTableOrderingFromSorter,
  withSortOrder
} from "../../../../utils/table"
import { useElementSize } from "../../../Calendar/CalendarPage/hooks/use-element-size.ts"
import type {
  StatusChangeOptions,
  StatusChangeOutcome
} from "../../../Project/components/ProjectStatusSelector.tsx"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { ProjectsEmpty } from "../../components/ProjectsEmpty.tsx"
import { PROJECT_STATUSES_OBJ } from "../../constants/project-statuses.ts"
import {
  canEditProjectPrice,
  canSeeProjectBudget
} from "../../helpers/project-budget-visibility.ts"
import {
  type ProjectBulkResult,
  type ProjectFilters
} from "../../redux/projects.types.ts"
import { type ProjectListItem } from "../../types/types.ts"
import { ProjectMobileList } from "../redesign/ProjectMobileList.tsx"
import { ProjectRowDetails } from "../redesign/project-cells.tsx"
import {
  PROJECT_COMMAND_SURFACE,
  REDESIGN_DEFAULT_HIDDEN_COLUMNS,
  detailsActivity,
  detailsFinancial,
  getRedesignColumns,
  hasRowDetails,
  tableTierFor
} from "../redesign/redesign-columns.tsx"
import {
  type ProjectEditableRowData,
  ProjectListEditableRow
} from "./ProjectListEditableRow.tsx"
import {
  type ProjectBulkActionParams,
  ProjectsBulkActionBar
} from "./ProjectsBulkActionBar.tsx"
import { PROJECT_COLUMN_KEYS, getColumns } from "./columns.tsx"

/**
 * Columns a user sees only after switching them on in Table settings. With all
 * of them on, the Active list was 1,682px wide, so Sales Price and Progress
 * started off-screen on a 1440px laptop. What is left — customer, project,
 * project manager, status, last activity (the list's default sort, which must
 * stay visible), sales price, progress — fits there. A saved layout wins
 * either way.
 */
export const PROJECT_DEFAULT_HIDDEN_COLUMNS = [
  PROJECT_COLUMN_KEYS.uid,
  PROJECT_COLUMN_KEYS.endDate,
  PROJECT_COLUMN_KEYS.created,
  PROJECT_COLUMN_KEYS.createdBy
]

/** The Archive sorts by Date Created and adds two columns of its own, so it
 *  keeps the defaults it had. */
const PROJECT_ARCHIVE_DEFAULT_HIDDEN_COLUMNS = [PROJECT_COLUMN_KEYS.endDate]

// Its own key: the Archive renders two columns Active does not, and a shared key
// would apply a saved visibility and order to columns that are not there.
const PROJECT_ARCHIVE_SURFACE = `${TABLE_SURFACES.projects}-archive`

// The redesign's own key: a layout saved against the old column set would
// otherwise hide or reorder columns this one does not have.

interface ProjectListTableProps {
  loading: boolean
  total: number
  projectPermissions: PermissionProjectPermissions
  ordering: string | null
  filters: ProjectFilters
  filterOptions: Record<string, BaseOptionType[]>
  projects: ProjectListItem[]
  next?: string | null
  retryNext?: string | null
  /** The first page failed to load, so an empty table is not an empty account. */
  loadFailed?: boolean
  onRetry?: () => void
  onClearFilters?: () => void
  onLoadMore: () => void
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
  onStatusChange: (
    id: string,
    status: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome>
  archived?: boolean
  // Bulk-action wiring — supplied by the Active list only.
  selectedIds?: string[]
  onSelectedIdsChange?: (ids: string[]) => void
  onBulkAction?: (params: ProjectBulkActionParams) => Promise<ProjectBulkResult>
  /** The redesigned command-center columns, cells and mobile cards. */
  redesign?: boolean
  /** How many of the quick view / My projects narrow the rows, for the empty state. */
  narrowingViews?: number
  /** Whether Table settings shows Schedule; Next milestone folds into it only then. */
  scheduleShown?: boolean
  /** Lets the page header open Table settings. */
  tableSettingsControl?: {
    open: boolean
    onOpenChange: (open: boolean) => void
  }
}

export function ProjectListTable({
  projects,
  loading,
  projectPermissions,
  ordering,
  filters,
  total,
  filterOptions,
  next,
  retryNext,
  loadFailed,
  onRetry,
  onClearFilters,
  onLoadMore,
  onOrderingChange,
  onSelectRow,
  onStatusChange,
  archived = false,
  selectedIds = [],
  onSelectedIdsChange,
  onBulkAction,
  redesign = false,
  narrowingViews = 0,
  scheduleShown = true,
  tableSettingsControl
}: ProjectListTableProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const [editingID, setEditingID] = useState<string>("")

  const moduleAccess = projectPermissions?.projects
  const moduleFeatures = projectPermissions?.features
  const showProgress = !!moduleFeatures?.project_show_progress
  const showBudget = canSeeProjectBudget(projectPermissions)

  const onChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ProjectListItem> | SorterResult<ProjectListItem>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: loading,
    next: next,
    loadMore: onLoadMore,
    fullPage: true
  })

  const tableSettings = useMemo(
    () => ({
      onRow: (record: ProjectListItem) => {
        return {
          record,
          editingID,
          assignedPMAllowed: moduleFeatures?.project_assign_pm,
          priceEditAllowed: canEditProjectPrice(projectPermissions),
          filterOptions,
          onCancelEdit: () => setEditingID(""),
          onUpdateRow: (record: ProjectEditableRowData) =>
            onSelectRow(record, "update"),
          ...(moduleAccess?.project_edit
            ? {
                onDoubleClick: () => {
                  if (editingID) return
                  return setEditingID(record?.id)
                }
              }
            : {})
        }
      }
    }),
    [
      moduleFeatures?.project_assign_pm,
      moduleAccess?.project_edit,
      onSelectRow,
      editingID,
      filterOptions,
      projectPermissions
    ]
  )

  const showMilestones =
    !!projectPermissions?.milestones?.project_milestone_view

  const commandLayout = redesign && !archived
  // Tablet portrait and phones take the card list; the table starts at the
  // width a tablet held sideways gives it.
  const onPhone = commandLayout && screens.lg === false
  // jsdom measures nothing, so a test starts on the full table.
  const { ref: sizeRef, size } = useElementSize({ width: 1600, height: 0 })
  const tier = commandLayout ? tableTierFor(size.width) : "full"
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const toggleDetails = useCallback(
    (id: string) =>
      setExpandedIds((open) =>
        open.includes(id) ? open.filter((other) => other !== id) : [...open, id]
      ),
    []
  )

  const tableColumns = useMemo(
    () =>
      withSortOrder(
        (commandLayout ? getRedesignColumns : getColumns)({
          tier,
          scheduleShown,
          expandedIds,
          onToggleDetails: onPhone ? undefined : toggleDetails,
          showMilestones,
          t,
          onStatusChange,
          onSelectRow,
          editAllowed: !!moduleAccess?.project_edit,
          projectPermissions,
          archived,
          archiveAllowed: !!moduleFeatures?.project_archive,
          restoreAllowed: !!moduleFeatures?.project_archive,
          deleteAllowed: !!moduleAccess?.project_delete,
          showProgress,
          showBudget,
          statuses: filterOptions?.project_status || []
        }),
        ordering
      ),
    [
      t,
      ordering,
      filterOptions?.project_status,
      projectPermissions,
      moduleAccess?.project_edit,
      moduleAccess?.project_delete,
      moduleFeatures?.project_archive,
      archived,
      showProgress,
      showBudget,
      showMilestones,
      commandLayout,
      tier,
      scheduleShown,
      expandedIds,
      onPhone,
      toggleDetails,
      onSelectRow,
      onStatusChange
    ]
  )

  // The Archive keeps `EntityTable`'s own on-demand selection (shown, never
  // acted on). The Active list lifts the ids so its bulk bar can use them.
  const bulkEnabled = !archived && !!onBulkAction && !!onSelectedIdsChange

  const rowSelection = useMemo(
    () =>
      bulkEnabled
        ? {
            selectedRowKeys: selectedIds,
            preserveSelectedRowKeys: true as const,
            onChange: (keys: React.Key[]) =>
              onSelectedIdsChange?.(keys.map(String)),
            // A selection cannot be edited while a row is in inline edit; the
            // ticks it already holds stay visible, they just cannot change.
            // An archived row (the Closed quick view) takes no bulk edit.
            getCheckboxProps: (record: ProjectListItem) => ({
              disabled: !!editingID || !!record.is_archived,
              "aria-label": t("projectsCommand.selectRow", {
                name: record.lead_name
              })
            })
          }
        : undefined,
    [bulkEnabled, selectedIds, onSelectedIdsChange, editingID, t]
  )

  const bulkStatusOptions = useMemo(
    () =>
      Object.values(PROJECT_STATUSES_OBJ).map(({ label, value }) => ({
        label,
        value
      })),
    []
  )

  const projectManagerOptions = useMemo(
    () =>
      (filterOptions?.project_manager ?? []).map((option) => ({
        label: String(option.label ?? ""),
        value: String(option.value ?? "")
      })),
    [filterOptions?.project_manager]
  )

  const projectNames = useMemo(
    () =>
      Object.fromEntries(
        (projects ?? []).map((project) => [project.id, project.lead_name])
      ),
    [projects]
  )

  const emptyState = (
    <ProjectsEmpty
      hasSearch={!!filters?.q}
      filters={filters}
      narrowingViews={narrowingViews}
      loadFailed={loadFailed}
      onRetry={onRetry}
      onClearFilters={onClearFilters}
    />
  )

  // A wide table squeezed onto a phone stops being readable; cards keep each
  // project's identity, status, schedule, milestone, manager and progress.
  if (onPhone) {
    const actionsColumn = tableColumns[tableColumns.length - 1]
    return (
      <ProjectMobileList
        projects={projects ?? []}
        loading={loading}
        hasMore={!!next}
        showMilestones={showMilestones}
        showProgress={showProgress}
        actionsColumn={actionsColumn}
        empty={emptyState}
        onLoadMore={onLoadMore}
      />
    )
  }

  return (
    <div ref={sizeRef}>
      <div ref={tableRef}>
        <EntityTable<ProjectListItem>
          surface={
            archived
              ? PROJECT_ARCHIVE_SURFACE
              : commandLayout
                ? PROJECT_COMMAND_SURFACE
                : TABLE_SURFACES.projects
          }
          hideConfigButton={commandLayout}
          configControl={commandLayout ? tableSettingsControl : undefined}
          // The redesign always shows its checkbox column, as the mockup does.
          selectionMode={commandLayout ? "always" : "on-demand"}
          expandable={
            commandLayout && hasRowDetails(tier, showBudget)
              ? {
                  expandedRowKeys: expandedIds,
                  showExpandColumn: false,
                  expandedRowRender: (record: ProjectListItem) => (
                    <ProjectRowDetails
                      project={record}
                      showFinancial={showBudget && detailsFinancial(tier)}
                      showActivity={detailsActivity(tier)}
                    />
                  )
                }
              : undefined
          }
          selectionFilters={filters}
          rowSelection={rowSelection}
          tab={DEFAULT_TAB}
          components={{
            body: projects?.length ? { row: ProjectListEditableRow } : {}
          }}
          columns={tableColumns}
          defaultHiddenColumns={
            archived
              ? PROJECT_ARCHIVE_DEFAULT_HIDDEN_COLUMNS
              : commandLayout
                ? REDESIGN_DEFAULT_HIDDEN_COLUMNS
                : PROJECT_DEFAULT_HIDDEN_COLUMNS
          }
          // Measured, not declared: the Archive columns, Sales Price and Progress
          // come and go, and so does whatever a user hides in Table settings.
          fitScrollX
          // The redesign shows skeleton rows instead of a spinner over an empty
          // state that has not been earned yet.
          loading={commandLayout ? false : loading && !projects?.length}
          dataSource={projects}
          scroll={{
            scrollToFirstRowOnChange: true,
            y: tableHeight
          }}
          onChange={onChangeTable}
          onScroll={onTableScroll}
          withOutPagination
          pagination={false}
          onRow={tableSettings.onRow}
          rowHoverable={true}
          locale={{
            emptyText:
              commandLayout && loading ? (
                <div aria-busy className="flex flex-col gap-5 px-4 py-3">
                  {[0, 1, 2, 3, 4, 5].map((row) => (
                    <Skeleton
                      key={row}
                      active
                      title={false}
                      paragraph={{ rows: 2, width: ["45%", "70%"] }}
                    />
                  ))}
                </div>
              ) : (
                emptyState
              )
          }}
          className={cn({
            "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
              !projects?.length,
            // Two-line identity rows at the spec's ~56-60px instead of ~68px.
            "[&_.ant-table-tbody>tr>td]:!py-2": commandLayout,
            // A quiet header: light neutral, small uppercase labels, darker
            // than body secondary text. Sorting shows in the icon and aria-sort,
            // never as a tinted column.
            [[
              "[&_.ant-table-thead>tr>th]:!bg-slate-50",
              "[&_.ant-table-thead>tr>th]:!text-xs",
              "[&_.ant-table-thead>tr>th]:!font-semibold",
              "[&_.ant-table-thead>tr>th]:!uppercase",
              "[&_.ant-table-thead>tr>th]:!tracking-wide",
              "[&_.ant-table-thead>tr>th]:!text-slate-600",
              "[&_.ant-table-thead>tr>th]:!whitespace-nowrap",
              "[&_.ant-table-row:not(:hover):not(.ant-table-row-selected)>td.ant-table-column-sort]:!bg-transparent",
              "[&_.ant-table-expanded-row>td]:!bg-slate-50",
              "[&_.ant-table-row-selected>td]:!bg-teal-50"
            ].join(" ")]: commandLayout
          })}
        />
        <TableResultCount
          loaded={projects?.length ?? 0}
          total={total}
          loading={loading}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
        {bulkEnabled ? (
          <ProjectsBulkActionBar
            selectedIds={selectedIds}
            projectNames={projectNames}
            statusOptions={bulkStatusOptions}
            projectManagerOptions={projectManagerOptions}
            editAllowed={!!moduleAccess?.project_edit}
            assignPmAllowed={!!moduleFeatures?.project_assign_pm}
            archiveAllowed={!!moduleFeatures?.project_archive}
            onClear={() => onSelectedIdsChange?.([])}
            onAction={onBulkAction!}
          />
        ) : null}
      </div>
    </div>
  )
}
